import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { Button } from "../ui/button";
import { motion } from "framer-motion";
// import { useGetSearchProducts } from "@/hooks/ApiHooks/productHooks/getSearchProductsHook";
import { useRouter } from "next/navigation";
import { NoImage } from "../../../public";
import HeaderSearchSkelton from "../common/skeltons/HeaderSearchSkelton";
import { useProductListingStore } from "../../zustand/productListingStore";
import { useNewGetAllCategories } from "@/hooks/ApiHooks/newProductListingHooks/newGetAllCategoriesHook";
import { useNewGetAllFilteredProducts } from "@/hooks/ApiHooks/newProductListingHooks/getNewRequiredProductHook";
import { Category } from "@/types/allProductPageTypes";
import Loader from "./Loader";

interface HeaderSearchAndCategoryProps {
  onSearchFocusChange?: (isFocused: boolean) => void;
}

const HeaderSearchAndCategory = ({ onSearchFocusChange }: HeaderSearchAndCategoryProps) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { setCategoryIdToStore, setSubCategoryIdToStore } = useProductListingStore();

  const router = useRouter();
  const menuRef = useRef(null);

  // Debounce search term so API isn't called on every keystroke
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearchTerm(next);
    }, 1000);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const { data: newCategoryData } = useNewGetAllCategories();
  const { data: searchData } = useNewGetAllFilteredProducts({ search: debouncedSearchTerm, pageNumber: 1 });

  useEffect(() => {
    const preventScroll = (event: Event) => {
      event.preventDefault();
    };

    if (isScrollLocked) {
      document.addEventListener("wheel", preventScroll, { passive: false });
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("keydown", (event) => {
        if (["ArrowUp", "ArrowDown", "Space"].includes(event.key)) {
          preventScroll(event);
        }
      });
    } else {
      document.removeEventListener("wheel", preventScroll);
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("keydown", preventScroll);
    }

    return () => {
      document.removeEventListener("wheel", preventScroll);
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("keydown", preventScroll);
    };
  }, [isScrollLocked]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !(menuRef.current as HTMLElement).contains(event.target as Node)) {
        setOpenMenu(null);
        setIsScrollLocked(false);
      }
    }

    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  const toggleMenu = (menu: string) => {
    // Only open menu if it's closed
    if (openMenu !== menu) {
      setOpenMenu(menu);
      setIsScrollLocked(true);
      // Notify parent component about search focus state
      if (onSearchFocusChange) {
        onSearchFocusChange(menu === "search" || menu === "all-products");
      }
    }
  };

  const handleCloseOverly = () => {
    setOpenMenu(null);
    setIsScrollLocked(false);
    setSearchTerm("");
    // Notify parent component that search is no longer focused
    if (onSearchFocusChange) {
      onSearchFocusChange(false);
    }
  };

  const processedCategories = (Array.isArray(newCategoryData?.result) ? newCategoryData?.result : [])
    .filter((category: Category) =>
      category?.name !== "Accessories" &&
      category?.name !== "Metal Products"
    )
    .sort((a: Category, b: Category) => {
      if (a?.name === "All Products") return -1;
      if (b?.name === "All Products") return 1;
      return 0;
    });

  const SearchResultImage = ({ src, alt }: { src: string; alt: string }) => {
    const [isLoading, setIsLoading] = useState(true);
    return (
      <div className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
            <Loader className="scale-75" />
          </div>
        )}
        <Image
          src={src || NoImage}
          alt={alt}
          width={80}
          height={80}
          className={`!static !h-full w-full object-center transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
      </div>
    );
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Overlay */}
      {openMenu && (
        <div
          className="fixed w-full h-full left-0 top-0 bg-black bg-opacity-50 z-[140]"
          onClick={handleCloseOverly}
        ></div>
      )}

      {/* Navigation Menu */}
      <ul className="hidden lg:flex ">
        {/* Search Products */}
        <li>
          <button
            onClick={() => toggleMenu("search")}
            className={`group border-b border-[#DADADA] py-web-8 flex items-center lg:w-[26.19vw] h-full pr-web-23 relative after:content-[''] after:absolute after:right-0 after:bottom-[.4vw] after:w-web-1 after:h-[80%] after:bg-[#DADADA] z-[145] pt-web-8 before:content-[''] before:absolute before:bottom-0 before:left-0  ${openMenu === "search" ? "before:w-full" : "before:w-0"} after:transition-all after:duration-200 before:h-web-2 before:bg-PrimaryLightBlue `}
          >
            <input
              type="text"
              placeholder="Search Products..."
              className="text-web-15 font-medium tracking-[0.23vw] grow h-full outline-none uppercase bg-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div
              className="w-web-18 transition-all duration-200 group-hover:scale-105 relative"
              onClick={() => {
                router.push(`/product-category/all-products`);
                setCategoryIdToStore(1525);
                setSubCategoryIdToStore(0);
              }}
            >
              <Image
                src="/images/search.svg"
                alt="search-icon"
                width={18}
                height={18}
                className="static h-auto"
              />
            </div>
          </button>

          {openMenu === "search" && (
            <motion.div
              initial={{ opacity: 0.7, }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="fixed left-0 w-full z-[141] top-web-46 flex justify-center"
            >
              <div className=" w-[92.75vw] bg-white relative">
                {/* Close Icon */}
                <button
                  onClick={handleCloseOverly}
                  className="absolute -top-[9px] -right-[9px] z-[150] w-web-38 h-web-38 flex items-center justify-center bg-PrimaryLightBlue rounded-full hover:bg-blue-600 transition-colors duration-200"
                >

                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-4 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>

                </button>

                <ul className="gap-web-20 flex pt-[10.98vw] pb-[3vw] px-[10.46vw] flex-wrap ">

                  {
                    searchData
                      ? searchData?.data?.result?.items?.map((product: { id: string; name: string; post_title: string; slug: string; images: string; }) => (
                        <li key={product.id}
                          className="w-[calc(100%/4-.8vw)] bg-white rounded-web-10 border border-[#E4E4E4] flex gap-web-11 p-web-10"
                          onClick={() => {
                            router.push(`/details/${product?.slug || product?.name}`);
                            handleCloseOverly();
                          }}
                        >
                          <div className="px-1 max-w-web-80 min-w-web-80 max-h-web-80 min-h-web-80 rounded-web-10 overflow-hidden cursor-pointer">
                            <SearchResultImage
                              src={product?.images}
                              alt="product"
                            />
                          </div>
                          <span className="text-[#323232] text-web-16 font-medium leading-[120%] capitalize cursor-pointer">
                            {product.post_title}
                          </span>
                        </li>
                      ))
                      : (searchTerm
                        ? <div className="-mt-web-70 mb-web-65"><HeaderSearchSkelton /></div>
                        : "")
                    // : <HeaderSearchSkelton />
                    // : <div className="w-full flex justify-center items-center text-web-32 font-semibold">Search for products...</div>
                  }

                </ul>
                {
                  Array.isArray(searchData) && searchData.length > 0
                    ?
                    <div className="mt-web-65 mb-web-65">
                      <Button variant={"secondaryLinkBtnBlue"}
                        onClick={() => {
                          router.push(`/product-category/all-products/1525`);
                          setCategoryIdToStore(1525);
                          setSubCategoryIdToStore(0);
                          handleCloseOverly();
                        }}
                      >
                        more products
                        <div className="w-mob-7 sm:w-tab-7 lg:w-web-7">
                          <Image
                            src="/images/chevron-right-blue.svg"
                            alt="Chevron"
                            width={7}
                            height={10}
                            className="w-full h-auto"
                          />
                        </div>
                      </Button>
                    </div>
                    :
                    (
                      searchTerm.trim().length > 0 && Array.isArray(searchData) && searchData.length === 0 && (
                        <div className="mb-web-65">
                          <Button variant={"secondaryLinkBtnBlue"}
                            onClick={() => {
                              handleCloseOverly();
                            }}
                          >
                            No Products Found
                            <div className="w-mob-7 sm:w-tab-7 lg:w-web-7">
                              <Image
                                src="/images/chevron-right-blue.svg"
                                alt="Chevron"
                                width={7}
                                height={10}
                                className="w-full h-auto"
                              />
                            </div>
                          </Button>
                        </div>
                      )
                    )
                }
              </div>
            </motion.div>
          )}
        </li>

        {/* All Products */}
        <li>
          <button
            onClick={() => toggleMenu("all-products")}
            className={` border-b border-[#DADADA] py-web-10 flex items-center text-web-15 font-medium leading-[120%] uppercase text-PrimaryLightBlue tracking-[0.156vw] pl-web-22 relative z-[145] after:content-[''] after:absolute after:bottom-0 after:left-0 ${openMenu === "all-products" ? "after:w-full" : "after:w-0"}  after:h-web-2 after:bg-PrimaryLightBlue `}
          >
            All Products
          </button>

          {openMenu === "all-products" && (
            <motion.div
              className="fixed left-0 w-full z-[141] top-web-46 flex justify-center"
              initial={{ opacity: .7 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: .4 }}
            >
              <div className=" w-[92.75vw] bg-white pb-web-65 relative">
                {/* Close Icon */}
                <button
                  onClick={handleCloseOverly}
                  className="absolute -top-[9px] -right-[9px] z-[150] w-web-38 h-web-38 flex items-center justify-center bg-PrimaryLightBlue rounded-full hover:bg-blue-600 transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-4 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* h-[30.8vw] */}
                {/* Categories List */}
                <ul className="flex pt-[10.98vw] px-[10.46vw] flex-wrap gap-web-42 justify-center">

                  {/* {["Ceiling System", "Drywall Partition"].map(
                    (category, i) => (
                      <li
                        key={i}
                        className="w-[calc(100%/4-1.7vw)] bg-white rounded-web-10 border border-[#E4E4E4] flex items-center gap-web-11 lg:gap-web-20 p-web-21"
                      >
                        <div className="w-web-54 h-auto">
                          <Image
                            src={`/images/${category
                              .toLowerCase()
                              .replace(" ", "-")}.svg`}
                            alt={category}
                            width={54}
                            height={54}
                          />
                        </div>
                        <span className="block text-[#323232] text-web-20 leading-[150%] font-medium">
                          {category}
                        </span>
                      </li>
                    )
                  )} */}

                  {
                    processedCategories
                      ? processedCategories.map((category: { id: string; name: string; slug: string; image?: { src: string; }; thumbnail?: string }) => (
                        <li
                          key={category.id}
                          className="w-[calc(100%/4-1.7vw)] bg-white rounded-web-10 border border-[#E4E4E4] cursor-pointer flex items-center gap-web-11 lg:gap-web-20 p-web-21"
                          onClick={() => {
                            router.push(`/product-category/${category.slug}`);
                            // router.push(`/product-category/${category.slug}/${category.id}?categoryId=${category.id}`);
                            setCategoryIdToStore(Number(category.id));
                            setSubCategoryIdToStore(0);
                            handleCloseOverly();
                          }}
                        >
                          <div className="lg:w-web-50">
                            <div className="w-mob-32 sm:w-tab-32 lg:w-web-60 h-auto px-[3px]">
                              <Image
                                // src="/images/ceiling-system.svg"
                                src={category?.thumbnail || "/images/drywall-partition.svg"}
                                alt="Category"
                                width={54}
                                height={54}
                              // fill
                              // className="!static !h-auto"
                              />
                            </div>
                          </div>
                          <span className="block text-[#323232] text-mob-15 sm:text-tab-15 lg:text-web-20 leading-[150%] font-medium">
                            {category.name.replace(/&amp;/g, "&")}
                          </span>
                        </li>
                      ))
                      : ""
                  }

                </ul>
              </div>
            </motion.div>
          )}
        </li>
      </ul>
    </div>
  );
};

export default HeaderSearchAndCategory;
